"use client";

import Image from "next/image";
import Link from "next/link";
import React, { useMemo } from "react";
import { Spinner } from "@/components/ui/spinner";
import { Pin } from 'lucide-react';

import { BannerCarousel } from "@/components/reusebale-components/bannerCarousel";
import { CallToAction } from "@/components/reusebale-components/CallToAction";
import { NewsUpdateCard } from "@/components/reusebale-components/news&updateCard";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import HeaderTitle from "@/components/reusebale-components/HeaderTitle";
import localEndPoints from '@/lib/localEndPoints';

import { useFetchSWR, useFetchSWRInfinite } from "@/lib/swrConfig";
import apiEndPoints from "@/lib/apiEndPoints";
import { BlogData } from "@/types/blogs";
import { bannerEnum as be } from "@/lib/utils";
import { BannerData } from "@/types/banner";

import fixNewsContent from '@/lib/SVG_Render';

const QUERY_LIMIT = 8;

export default function NewsPage() {
  // Banner data
  const { data: bannerData, isLoading } = useFetchSWR(apiEndPoints.BANNER(be.BLOG));
  const banner: BannerData = bannerData?.status === 200 ? bannerData.data : {};

  // Infinite Blogs
  const getKey = (pageIndex: number, previousPageData: any) => {
   
    if (previousPageData && previousPageData.data && previousPageData.data.length < QUERY_LIMIT) {
      return null;
    }
    return apiEndPoints.BLOGS(pageIndex + 1, QUERY_LIMIT);
  };

  const {
    data,
    size,
    setSize,
    isValidating,
    isLoading: isLoadingBlogsInitial, 
    error: blogsError,
  } = useFetchSWRInfinite(getKey, {
    revalidateOnFocus: false,
    revalidateOnMount: true, 
  });

  
  const isLoadingMore = isLoadingBlogsInitial || (size > 0 && data && typeof data[size - 1] === 'undefined');

  const allNews: BlogData[] = useMemo(() => {
    if (blogsError) return []; 
    return data ? data.flatMap((page) => page?.data || []) : [];
  }, [data, blogsError]); 

  const topNews = useMemo(() => {
    return allNews.filter((news) => news.is_pinned === true);
  }, [allNews]);

  const gridNews = useMemo(() => {
    return allNews.filter((news) => news.is_pinned === false);
  }, [allNews]);

  const isReachingEnd = data && (data[data.length - 1]?.data?.length < QUERY_LIMIT || data[data.length - 1]?.data?.length === 0);
  const isEmpty = !isLoadingBlogsInitial && allNews.length === 0 && !blogsError;


  // Unified loading state for the main content
  const showMainSpinner = isLoadingBlogsInitial && allNews.length === 0;

  // img drowing problem 

  return (
    <>
      {
        isLoading ? (
                <div className="h-screen flex items-center justify-center">
                      <Spinner />
                  </div>
                ) : (
                    <BannerCarousel
                    images={banner?.media?.map((media) => media.url)}
                    title={banner?.title}
                    description={banner?.details}
                    />
                )
            } 

      <div className="container mt-6 md:mt-10 lg:mt-16">
        {showMainSpinner ? (
          <div className="flex justify-center items-center h-64">
            <Spinner />
          </div>
        ) : isEmpty ? (
          <div className="text-center text-muted-foreground py-10">
            <p>No news or updates available at the moment.</p>
          </div>
        ) : (
          <>
            {/* Title */}
            {allNews.length > 0 && (
              <HeaderTitle title="News & Updates" url="" />
            )}

            {/* Latest News (Pinned) */}
            {topNews.length > 0 && (
              <div className="space-y-8">
                {topNews.map((news: BlogData) => (
                  <Link
                    key={news.id}
                    href={`${localEndPoints.NEWS}/${news.slug}`}
                    className="block"
                  >
                    <div className="flex flex-col md:flex-row items-center bg-transparent group gap-4 md:gap-8 rounded-2xl transition-all duration-300">
                      {/* Image Wrapper */}
                      <div className="relative w-full md:w-1/3 aspect-[3/2] rounded-xl overflow-hidden">
                        <Image
                          src={
                            news?.media && news.media.length > 0
                              ? news.media[0].url
                              : "default.jpg"
                          }
                          alt={news.title || news.slug || "News image"}
                          fill
                          sizes="(max-width: 768px) 100vw, 33vw"
                          loading="lazy"
                          className="object-cover transition-transform duration-300 ease-in-out bg-white group-hover:scale-110"
                        />
                      </div>

                      {/* Content */}
                      <div className="flex-1 p-2 md:p-0">
                        {/* Date + Pinned */}
                        <div className="text-muted-foreground text-xs md:text-sm flex items-center gap-2 flex-wrap">
                          <span>{news.published_at}</span>
                          {news.is_pinned && (
                            <div className="ml-1 px-2 py-0.5 flex items-center gap-1 text-xs md:text-sm bg-primary/10 text-primary rounded-full">
                              <Pin size={12} className="rotate-45" />
                              Pinned
                            </div>
                          )}
                        </div>

                        {/* Title */}
                        <h2 className="text-lg md:text-xl font-semibold mt-2 md:mt-3 line-clamp-2 group-hover:text-primary transition-colors">
                          {news.title}
                        </h2>

                        {/* Content Preview */}
                        {news.content && (
                          <div
                          className="prose text-muted-foreground text-sm mt-3 leading-relaxed line-clamp-5"
                          dangerouslySetInnerHTML={{ __html: fixNewsContent(news.content) }}
                        />
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* Divider */}
            {topNews.length > 0 && (
              <Separator className="my-6 md:my-10 lg:my-16" />
            )}

            {/* Older News Grid */}
            {gridNews.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
                {gridNews.map((news: BlogData) => (
                  <NewsUpdateCard key={news.id} news={news} />
                ))}
              </div>
            )}

            {/* See More Button */}
            {!isReachingEnd && gridNews.length > 0 && (
              <div className="flex justify-center mt-12">
                <Button
                  onClick={() => setSize(size + 1)}
                  disabled={isValidating || isLoadingMore} // Disable while any page is loading
                  className="px-6 py-3 border border-ring rounded-full text-sm font-medium bg-primary hover:bg-prime-hover cursor-pointer"
                >
                  {(isValidating || isLoadingMore) ? <Spinner /> : "See More"}
                </Button>
              </div>
            )}

            {/* Divider */}
            <Separator className="my-6 md:my-10 lg:my-16" />

            <div className="pb-6 md:pb-10 lg:pb-16">
              <CallToAction
                title="Join Us in Making a Difference"
                description="Your support can create lasting change in our communities. Get involved and make an impact today!"
                buttonText="Contact"
              />
            </div>
          </>
        )}
      </div>
    </>
  );
}